/**
 * DITA HTML Processor
 * 
 * This utility processes DITA HTML files by:
 * 1. Extracting source_id references from data-dita-source_id attributes
 * 2. Fetching presigned URLs for each source_id
 * 3. Replacing src/href attributes with the fetched presigned URLs
 * 4. Supporting lazy loading and batch processing for performance
 */

export interface DitaSourceRef {
  sourceId: string;
  element: HTMLElement;
  type: 'img' | 'anchor';
  originalSrc?: string;
  originalHref?: string;
}

export type DitaResourceCategory = 'dita-internal' | 'external' | 'broken-dita' | 'other';

export function isAbsolutePublicUrl(url: string | null | undefined): boolean {
  if (!url) return false;
  return /^https?:\/\//i.test(url.trim());
}

export function looksLikeDitaInternalReference(url: string | null | undefined): boolean {
  const value = (url ?? '').trim();
  if (!value) return false;
  if (isAbsolutePublicUrl(value)) return false;
  if (/^(mailto:|tel:|javascript:|data:|blob:|#)/i.test(value)) return false;

  if (/\.(html?|xml|dita|ditamap)(\?|#|$)/i.test(value)) return true;
  if (/^[0-9a-f-]{16,}\.[a-z0-9]+/i.test(value)) return true;
  if (/\.(png|jpe?g|gif|svg|webp|bmp|ico)(\?|#|$)/i.test(value)) return true;

  return false;
}

export function classifyDitaResource(element: HTMLElement): DitaResourceCategory {
  const sourceId = element.getAttribute('data-dita-source_id');
  if (sourceId && sourceId.trim().length > 0) {
    return 'dita-internal';
  }

  const tag = element.tagName.toLowerCase();
  const rawUrl =
    tag === 'img'
      ? element.getAttribute('src')
      : element.getAttribute('data-dita-href') || element.getAttribute('href');

  if (isAbsolutePublicUrl(rawUrl)) {
    return 'external';
  }
  if (looksLikeDitaInternalReference(rawUrl)) {
    return 'broken-dita';
  }
  return 'other';
}

/**
 * Extracts all DITA source references from the DOM
 */
export function extractDitaSourceRefsFromDOM(container: HTMLElement): DitaSourceRef[] {
  const sourceRefs: DitaSourceRef[] = [];

  // Find all images with data-dita-source_id that haven't been processed yet
  const images = container.querySelectorAll('img[data-dita-source_id]:not([data-dita-processed="true"])');
  images.forEach((img) => {
    const sourceId = img.getAttribute('data-dita-source_id');
    const originalSrc = img.getAttribute('src');
    if (sourceId) {
      sourceRefs.push({
        sourceId,
        element: img as HTMLElement,
        type: 'img',
        originalSrc: originalSrc || undefined,
      });
    }
  });

  // Find all anchors with data-dita-source_id that haven't been processed yet
  const anchors = container.querySelectorAll('a[data-dita-source_id]:not([data-dita-processed="true"])');
  anchors.forEach((anchor) => {
    const sourceId = anchor.getAttribute('data-dita-source_id');
    const originalHref = anchor.getAttribute('href');
    if (sourceId) {
      sourceRefs.push({
        sourceId,
        element: anchor as HTMLElement,
        type: 'anchor',
        originalHref: originalHref || undefined,
      });
    }
  });

  console.log(`📋 Extracted ${sourceRefs.length} DITA references:`, {
    images: sourceRefs.filter((r) => r.type === 'img').length,
    links: sourceRefs.filter((r) => r.type === 'anchor').length,
  });

  // Return all refs - each element needs to be updated individually
  return sourceRefs;
}

export interface DitaPresignedResource {
  url: string;
  filename?: string;
  extension?: string;
}

export async function fetchPresignedResource(
  sourceId: string,
  getPdfLink: (payload: any) => Promise<any>,
  advisorId?: string,
  maxRetries: number = 2
): Promise<DitaPresignedResource | null> {
  // Validate source_id
  if (!sourceId || typeof sourceId !== 'string' || sourceId.trim().length === 0) {
    console.warn(`⚠️ Invalid source_id:`, sourceId);
    return null;
  }

  let lastError: Error | null = null;

  for (let attempt = 0; attempt <= maxRetries; attempt++) {
    try {
      const payload: any = { 
        source_ids: [{ source_id: sourceId.trim() }] 
      };
      
      // Add advisor_id if provided
      if (advisorId) {
        payload.advisor_id = advisorId;
      }
      
      if (attempt > 0) {
        console.log(`🔄 Retry attempt ${attempt}/${maxRetries} for source_id: ${sourceId.substring(0, 20)}...`);
      }
      
      const apiResult = getPdfLink(payload);

      // Handle both RTK Query mutation results and plain promises
      const response =
        typeof (apiResult as any).unwrap === 'function'
          ? await (apiResult as any).unwrap()
          : await apiResult;

      const entry = response?.data?.[0];
      const presignedUrl = entry?.full_source_presigned_url;
      
      if (!presignedUrl || typeof presignedUrl !== 'string') {
        console.warn(`⚠️ No valid presigned URL returned for source_id: ${sourceId}`);
        return null;
      }

      // Validate URL format
      try {
        new URL(presignedUrl);
      } catch {
        console.warn(`⚠️ Invalid URL format returned for source_id: ${sourceId}`);
        return null;
      }

      return {
        url: presignedUrl,
        filename: typeof entry?.filename === 'string' ? entry.filename : undefined,
        extension:
          typeof entry?.source_extension === 'string'
            ? entry.source_extension.toLowerCase()
            : undefined,
      };
    } catch (error) {
      lastError = error as Error;
      console.error(`❌ Attempt ${attempt + 1}/${maxRetries + 1} failed for source_id: ${sourceId}`, error);
      
      // Don't retry on 4xx errors (client errors)
      if (error && typeof error === 'object' && 'status' in error) {
        const status = (error as any).status;
        if (status >= 400 && status < 500) {
          console.warn(`⚠️ Client error ${status}, not retrying for source_id: ${sourceId}`);
          break;
        }
      }
      
      // Wait before retry (exponential backoff)
      if (attempt < maxRetries) {
        const delay = Math.min(1000 * Math.pow(2, attempt), 5000);
        await new Promise(resolve => setTimeout(resolve, delay));
      }
    }
  }

  console.error(`❌ All retry attempts failed for source_id: ${sourceId}`, lastError);
  return null;
}

/**
 * Processes DITA source references sequentially one by one
 * Caches API results to avoid duplicate calls for same source_id
 */
export async function processDitaSourceRefsBatch(
  refs: DitaSourceRef[],
  getPdfLink: (payload: any) => Promise<any>,
  advisorId?: string,
  concurrencyLimit: number = 5,
  onError?: (error: Error, sourceId: string) => void
): Promise<void> {
  // Group refs by source_id to handle duplicates efficiently
  const refsBySourceId = new Map<string, DitaSourceRef[]>();
  refs.forEach((ref) => {
    const existing = refsBySourceId.get(ref.sourceId) || [];
    existing.push(ref);
    refsBySourceId.set(ref.sourceId, existing);
  });

  console.log(`🔄 Processing ${refs.length} elements (${refsBySourceId.size} unique source IDs) - ONE BY ONE...`);

  // Process unique source_ids SEQUENTIALLY (one at a time)
  const uniqueSourceIds = Array.from(refsBySourceId.keys());
  let processed = 0;

  for (const sourceId of uniqueSourceIds) {
    const refsForThisId = refsBySourceId.get(sourceId) || [];
    
    try {
      processed++;
      // Fetch presigned URL once per source_id
      console.log(`🔄 [${processed}/${uniqueSourceIds.length}] Fetching presigned URL for:`, sourceId.substring(0, 20) + '...', `(${refsForThisId.length} elements)`);
      
      const resource = await fetchPresignedResource(sourceId, getPdfLink, advisorId);
      
      if (!resource?.url) {
        throw new Error(`Failed to get presigned URL for ${sourceId}`);
      }

      const presignedUrl = resource.url;
      
      // Update ALL elements with this source_id
      refsForThisId.forEach((ref) => {
        try {
          if (ref.type === 'img') {
            ref.element.setAttribute('src', presignedUrl);
            ref.element.setAttribute('data-dita-processed', 'true');
            ref.element.removeAttribute('data-dita-error'); // Clear any previous error
            
            // Add loading attribute for better performance
            if (!ref.element.hasAttribute('loading')) {
              ref.element.setAttribute('loading', 'lazy');
            }
          } else if (ref.type === 'anchor') {
            ref.element.setAttribute('href', presignedUrl);
            ref.element.setAttribute('data-dita-processed', 'true');
            ref.element.setAttribute('target', '_blank');
            ref.element.setAttribute('rel', 'noopener noreferrer');
            ref.element.setAttribute('data-dita-href', presignedUrl);
            if (resource.filename) {
              ref.element.setAttribute('data-dita-filename', resource.filename);
            }
            if (resource.extension) {
              ref.element.setAttribute('data-dita-extension', resource.extension);
            }
            (ref.element as HTMLElement).style.cursor = 'pointer';
            ref.element.removeAttribute('data-dita-error'); // Clear any previous error
          }
        } catch (updateError) {
          console.error(`❌ Failed to update element for source_id: ${sourceId}`, updateError);
        }
      });
      
      console.log(`✅ [${processed}/${uniqueSourceIds.length}] Updated ${refsForThisId.length} element(s) for:`, sourceId.substring(0, 20) + '...');
      
    } catch (error) {
      console.error(`❌ [${processed}/${uniqueSourceIds.length}] Failed to process source_id:`, sourceId, error);
      
      // Mark failed elements with error indicator
      refsForThisId.forEach((ref) => {
        try {
          ref.element.setAttribute('data-dita-error', 'true');
          ref.element.setAttribute('data-dita-processed', 'true'); // Mark as processed to avoid retry
          
          if (ref.type === 'img') {
            ref.element.setAttribute('alt', `Failed to load image: ${sourceId.substring(0, 30)}...`);
            ref.element.style.border = '1px dashed #ff6b6b';
            ref.element.style.padding = '4px';
            ref.element.style.background = '#ffe0e0';
          } else if (ref.type === 'anchor') {
            ref.element.style.color = '#ff6b6b';
            ref.element.style.textDecoration = 'line-through';
            ref.element.setAttribute('title', `Failed to load link for: ${sourceId.substring(0, 30)}...`);
          }
        } catch (errorMarkingError) {
          console.error(`❌ Failed to mark error for element:`, errorMarkingError);
        }
      });
      
      if (onError) {
        onError(error as Error, sourceId);
      }
    }
  }
  
  console.log(`✅ Completed processing ${refs.length} elements (${processed} API calls made)`);
}

/**
 * Creates an IntersectionObserver for lazy loading DITA references
 */
export function createDitaLazyLoader(
  callback: (entries: IntersectionObserverEntry[]) => void,
  rootMargin: string = '200px'
): IntersectionObserver {
  return new IntersectionObserver(callback, {
    rootMargin,
    threshold: 0.01,
  });
}

/** Default DITA content styles applied to `.dita-content` containers. */
export const DITA_DEFAULT_CONTENT_STYLES = `
  .dita-content {
    box-sizing: border-box;
    padding: 1rem 1.25rem;
    color: #111827;
    font-size: 0.95rem;
    line-height: 1.7;
    background: #ffffff;
  }

  .dita-content h1,
  .dita-content h2,
  .dita-content h3,
  .dita-content h4,
  .dita-content h5,
  .dita-content h6 {
    color: #111827;
    font-weight: 600;
    line-height: 1.35;
    margin: 1rem 0 0.5rem;
  }

  .dita-content h1 { font-size: 1.5rem; }
  .dita-content h2 { font-size: 1.25rem; }
  .dita-content h3 { font-size: 1.125rem; }

  .dita-content p {
    margin: 0.5rem 0;
    color: #374151;
  }

  .dita-content ul,
  .dita-content ol {
    margin: 0.5rem 0 0.75rem;
    padding-left: 1.5rem;
    color: #374151;
  }

  .dita-content ul {
    list-style-type: disc !important;
    list-style-position: outside !important;
  }

  .dita-content ol {
    list-style-type: decimal !important;
    list-style-position: outside !important;
  }

  .dita-content li {
    display: list-item !important;
    margin: 0.25rem 0;
  }

  .dita-content table {
    width: 100%;
    border-collapse: collapse;
    margin: 0.75rem 0 1rem;
    font-size: 0.92rem;
  }

  .dita-content th,
  .dita-content td {
    border: 1px solid #e5e7eb;
    padding: 0.5rem 0.625rem;
    text-align: left;
    vertical-align: top;
  }

  .dita-content th {
    background: #f9fafb;
    font-weight: 600;
    color: #111827;
  }

  .dita-content a {
    color: #006FEE;
    text-decoration: underline;
    cursor: pointer;
  }

  .dita-content a:visited {
    color: #005BC4;
  }

  .dita-content a:hover {
    color: #005BC4;
    text-decoration: underline;
  }

  .dita-content img {
    max-width: 100%;
    height: auto;
    border-radius: 0.5rem;
  }

  .dita-content code {
    background: #f3f4f6;
    padding: 0.1rem 0.3rem;
    border-radius: 0.25rem;
    font-size: 0.85em;
  }

  .dita-content a[target="_blank"]::after {
    content: " ↗";
    font-size: 0.8em;
    vertical-align: super;
  }

  /* Dark mode: darken the DITA "paper" and force default content text light.
     Explicitly-colored source spans keep their own inline colors. */
  .dark .dita-content {
    color: #c9d1d9;
    background: #161b22;
  }

  .dark .dita-content h1,
  .dark .dita-content h2,
  .dark .dita-content h3,
  .dark .dita-content h4,
  .dark .dita-content h5,
  .dark .dita-content h6 {
    color: #f0f6fc;
  }

  .dark .dita-content p,
  .dark .dita-content ul,
  .dark .dita-content ol,
  .dark .dita-content li {
    color: #c9d1d9;
  }

  .dark .dita-content th,
  .dark .dita-content td {
    border-color: #30363d;
  }

  .dark .dita-content th {
    background: #21262d;
    color: #f0f6fc;
  }

  .dark .dita-content a {
    color: #58a6ff;
  }

  .dark .dita-content a:visited,
  .dark .dita-content a:hover {
    color: #79c0ff;
  }

  .dark .dita-content code {
    background: #21262d;
    color: #e6edf3;
  }
`;

